import { useState, useEffect, useRef } from "react";

/**
 * @description
 * - useScrollable exposes a flag to determine if an element is scrollable or not from a given ref.
 * - helpful for conditional behavior based on scrollable state, example: elements need to be focusable if they are scrollable for keyboard navigation.
 * - Note: There was an attempt to implement this using ResizeObserver, but for various reasons, there was to much unpredictability in the behavior, so a useEffect with attached dependencies is used.
 *
 * @param [deps=[]] - dependencies to trigger a re-check of the scrollable state. Be carefull with this, as it can lead to unpredictable behavior.
 * @param [useRefCurrentForDeps=false] - if true, the ref.current will be included in the dependencies list, if false not (default).
 * @param [blockEffect=false] - if true, the effect will not be executed, if false (default) it will be. Can be used to conditionally block the effect.
 * @TypeParam T - is extended not statically typed to HTMLElement for a reason: Some elements like HTMLTableElement would not be assignable to HTMLElement.
 */
export default function useScrollable<T extends HTMLElement>(
  deps: unknown[] = [],
  blockEffect = false,
  useRefCurrentForDeps = false,
) {
  const ref = useRef<T>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const depsList = useRefCurrentForDeps ? [ref.current, ...deps] : deps;

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    if (blockEffect) return;

    setIsScrollable(element.scrollHeight > element.clientHeight);

    // cannot be implemented any other way, depending of the surrounding context ref.current needs to be checked, in other cases not or additional external dependencies are needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, depsList);

  return { ref, isScrollable };
}
